import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';

export function swapItemsInArray(array: any[], fromIndex: number, toIndex: number) {
  const temp = array[fromIndex];
  array[fromIndex] = array[toIndex];
  array[toIndex] = temp;
}

export function cloneAbstractControl(control: AbstractControl<any>): AbstractControl<any> {
  if (control instanceof FormGroup) {
    const clonedGroup = new FormGroup({});
    Object.keys(control.controls).forEach(controlName => {
      clonedGroup.addControl(controlName, cloneAbstractControl(control.get(controlName)!));
    });
    return clonedGroup;
  }
  return new FormControl(control.value);
}

export function replaceEmptyStringWithNull(data: any): void {
  const newData = JSON.parse(JSON.stringify(data));
  for (const key in newData) {
    if (Array.isArray(newData[key])) {
      newData[key].forEach(obj => {
        for (const prop in obj) {
          if (obj[prop] === '') {
            obj[prop] = null;
          }
        }
      });
    }
  }
  return newData;
}

export function extractErrorMessage(error: any): string {
  if (typeof error === 'object' && error !== null) {
    for (const key in error) {
      if (error.hasOwnProperty(key)) {
        if (Array.isArray(error[key]) && error[key].length > 0) {
          return error[key][0];
        }
      }
    }
  }
  return '';
}

export const removeEmptyStringProperties = obj => {
  for (const key in obj) {
    if (obj[key] === '') {
      delete obj[key];
    }
  }
  return obj;
};

export const removeEmptyStringAndArrayProperties = (obj: any): any => {
  for (const key in obj) {
    const value = obj[key];

    if (value === '') {
      delete obj[key];
    } else if (Array.isArray(value) && value.length === 0) {
      delete obj[key];
    }
  }

  return obj;
};

export function urlValidator(checkForEmpty = false, requireWWW = false) {
  return (control: AbstractControl) => {
    const urlPattern = requireWWW
      ? /^(https?:\/\/)?www\.([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/
      : /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

    if (checkForEmpty && (!control.value || control.value.trim() === '')) {
      return null;
    }

    if (control.value && !urlPattern.test(control.value)) {
      return {invalidUrl: true}; // Return error if invalid
    }

    return null;
  };
}

export const loadBase64ImageAsFile = (
  base64Image: string,
  fileName: string = 'image.png',
  defaultMimeType: string = 'image/png',
) => {
  let base64Data: string;
  let mimeString: string;

  if (base64Image.includes(',')) {
    const [prefix, data] = base64Image.split(',');
    mimeString = prefix.includes(':') ? prefix.split(':')[1].split(';')[0] : defaultMimeType;
    base64Data = data;
  } else {
    mimeString = defaultMimeType;
    base64Data = base64Image;
  }

  const byteString = atob(base64Data);
  const byteArray = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([byteArray], {type: mimeString});
  const file = new File([blob], fileName, {type: mimeString});

  return file;
};

export const base64ToBlob = (base64: string, contentType: string) => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length).fill(0).map((_, i) => slice.charCodeAt(i));
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, {type: contentType});
};

export const startEndTimeRequiredValidator: ValidatorFn = (group: FormGroup): ValidationErrors | null => {
  const start = group.get('start')?.value;
  const end = group.get('end')?.value;
  const startTime = group.get('start_time')?.value;
  const endTime = group.get('end_time')?.value;

  // Helper to check if a value is empty or an invalid placeholder.
  const isEmptyOrInvalid = (value: string): boolean => {
    return !value || value.trim() === '' || value === '--:--' || value === 'NaN-NaN-NaN NaN:NaN:NaN';
  };

  // If both start and end are empty (or invalid), skip further validation.
  if (isEmptyOrInvalid(start) && isEmptyOrInvalid(end)) {
    return null;
  }

  const errors: any = {};

  // If one field is filled, then both start and end must have valid values.
  if (isEmptyOrInvalid(start)) {
    errors.start_required = 'Start date is required when end date is provided';
  }
  if (isEmptyOrInvalid(end)) {
    errors.end_required = 'End date is required when start date is provided';
  }

  // When either start or end is provided, also require the time fields.
  if (!startTime || startTime.trim() === '') {
    errors.start_time_required = 'Start time is required when start or end date is provided';
  }
  if (!endTime || endTime.trim() === '') {
    errors.end_time_required = 'End time is required when start or end date is provided';
  }

  // If both dates are valid, ensure the end date is not before the start date.
  if (!isEmptyOrInvalid(start) && !isEmptyOrInvalid(end)) {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (endDate < startDate) {
      errors.date_order = 'End date cannot be before start date';
    }
  }

  return Object.keys(errors).length ? errors : null;
};

export const endNotBeforeStartValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const start = group.get('start')?.value;
  const end = group.get('end')?.value;
  if (!start || !end) {
    return null;
  }
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }
  return endDate < startDate ? {endBeforeStart: 'End date cannot be before start date'} : null;
};

export function timeInDateTimeRangeValidator(day): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }

    const datetime = new Date(value.replace(' ', 'T'));
    const eventDayStart = new Date(`${day.performance_start.replace(' ', 'T')}`);
    const eventDayEnd = new Date(`${day.performance_end.replace(' ', 'T')}`);

    if (datetime && (datetime < eventDayStart || datetime > eventDayEnd)) {
      return {timeOutOfRange: true};
    }

    return null;
  };
}

export function validDateTimeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    if (value && value.trim() !== '') {
      if (value === '--:--' || value === 'NaN-NaN-NaN NaN:NaN:NaN') {
        return null;
      }

      const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
      if (!regex.test(value)) {
        return {invalidDateTime: 'Time must be in the format "YYYY-MM-DD HH:mm:ss"'};
      }
    }
    return null;
  };
}

export const getAppicWebsiteUrlBasedOnEnv = () => {
  const isLocalhost = window.location.host?.indexOf('localhost') > -1;
  if (isLocalhost) {
    return 'https://st0.dev.appic.events';
  }

  const suffix = '.business.beappic.com'; // eg. st0.dev
  const suffixIndex = window.location.host?.indexOf(suffix);
  const dashboardDomainFirstPart = suffixIndex > -1 ? window.location.host.substring(0, suffixIndex) : '';

  const isStaging = dashboardDomainFirstPart.indexOf('dev') > -1;

  return 'https://' + (isStaging ? dashboardDomainFirstPart + '.appic.events' : 'appic.events');
};

export const addMinutes = (d: Date, minutes: number): Date => {
  const t = new Date(d);
  t.setSeconds(0, 0);
  t.setMinutes(t.getMinutes() + minutes);
  return t;
};

export function toPromiseOnce<T>(obs: Observable<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    obs.pipe(take(1)).subscribe({
      next: resolve,
      error: reject,
    });
  });
}
